<script lang="ts">
	import { fly } from 'svelte/transition';
	import { clickOutside } from '$lib/utils/clickOutside';
	import { t, locale } from '$lib/i18n';
	import _ from 'lodash';

	export let questionOrder: number;
	export let options: any = [];
	export let value: string[] = [];
	export let maxSelectionCount = 10;
	export let style: 'content' | 'phone' = 'content';

	let showOptions = false;
	let selected: any = {};
	$: {
		// Update `selected` whenever `value` changes, so it reflects external resets.
		selected = value.reduce((acc: any, val: string) => ({ ...acc, [val]: val }), {});
		value = Object.values(selected).map((o: any) => o.toString());
	}
	const toggleOptionsMenu = _.throttle(
		(e: any = null) => {
			showOptions = !showOptions;
			if (e) {
				e.preventDefault();
				e.stopPropagation();
			}
		},
		200,
		{ trailing: false }
	);

	const updateOptions = (e: any) => {
		e.preventDefault();
		e.stopPropagation();
		const selectedOption = e.target.dataset.value;
		if (selected[selectedOption]) {
			// Deselect option
			const { [selectedOption]: val, ...rest } = selected;
			selected = rest;
		} else if (maxSelectionCount === 1) {
			// Single-selection mode
			selected = { [selectedOption]: selectedOption };
			toggleOptionsMenu();
		} else if (value.length < maxSelectionCount) {
			// Multi-selection mode
			selected[selectedOption] = selectedOption;
			if (value.length === maxSelectionCount - 1) {
				toggleOptionsMenu();
			}
		}
		value = Object.values(selected).map((o: any) => o.toString());
	};
</script>

<input
	type="hidden"
	name="question-{questionOrder}"
	id="question-{questionOrder}"
	value={value.join(',')}
	class:bg-slate-100={style === 'phone'}
/>
<div class="bg-[#ffffff] relative my-2 rounded-md">
	<button
		class="flex justify-between w-full px-4 py-2 text-xs rounded-md cursor-pointer"
		class:bg-slate-100={style === 'phone'}
		on:click={toggleOptionsMenu}
	>
		{#if Object.keys(selected).length === 0}
			<p class="text-gray-500 text-[0.9rem] my-1">
				{$t('common.select', { default: 'Please Select...' })}
			</p>
		{:else}
			<p class="text-black text-[0.9rem] !mb-0 overflow-hidden overflow-ellipsis whitespace-nowrap">
				{Object.values(selected).join(', ')}
			</p>
		{/if}

		<div class="flex items-center justify-end">
			<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
				><path d="M5 8l4 4 4-4z" /></svg
			>
		</div>
	</button>

	<select multiple class="hidden">
		{#each options as option}
			<option value={option}>{option}</option>
		{/each}
	</select>

	{#if showOptions}
		<!-- svelte-ignore a11y-no-static-element-interactions -->
		<div
			class="shadow-md absolute top-[20] w-full max-h-[250px] overflow-auto bg-white z-50 text-sm"
			transition:fly|global={{ duration: 200, y: 5 }}
			on:mousedown|preventDefault={updateOptions}
			use:clickOutside
			on:clicked_outside_event={toggleOptionsMenu}
			aria-roledescription="mulitselect options"
			role="listbox"
			tabindex="0"
		>
			<ul class="list-none p-0">
				{#each options as option}
					<li
						class="p-2 pl-6 text-black bg-white cursor-pointer flex items-center"
						class:text-main-dark={selected[option] || selected.selectedOption === option}
						class:!bg-slate-100={style === 'phone'}
						data-value={option}
					>
						<div 
							class="w-4 h-4 border border-gray-400 rounded mr-2 flex items-center justify-center"
							data-value={option}
						>
							{#if selected[option] || selected.selectedOption === option}
								<span class="block w-1.5 h-2.5 mb-0.5 bg-white transform rotate-45 border-b-2 border-r-2 border-main-dark"
								data-value={option}></span>
							{/if}
						</div>
						{option}
					</li>
				{/each}
			</ul>
		</div>
	{/if}
</div>
